/**
 * ReviewerProfile — the Review User's own profile page.
 *
 * View + edit: first/last name and profile photo. Email + role are
 * read-only identity facts. Photo flow: pick file → POST
 * /api/v1/files/upload (existing shared endpoint) → save the returned
 * file_url via PUT /users/me { profile_image_url } → dispatch
 * "reviewer-profile-updated" so the header avatar refreshes.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Skeleton,
  Space,
  Tag,
  Typography,
  Upload,
  message as antMessage,
} from "antd";
import {
  CameraOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { fetchData, updateData } from "../../api/apiService";
import { USERS } from "../../api/endpointMap";
import { getEmbedToken } from "../../api/api";

const { Title, Paragraph, Text } = Typography;

const MAX_IMAGE_MB = 4;

export default function ReviewerProfile() {
  const [form] = Form.useForm();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Name card starts read-only; the Edit button flips it into edit
  // mode (inputs + Save + Cancel). Successful save returns to
  // read-only.
  const [editingName, setEditingName] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchData(USERS.USER_INFO);
      setMe(data || {});
      form.setFieldsValue({
        first_name: data?.first_name || "",
        last_name: data?.last_name || "",
      });
    } catch (e) {
      antMessage.error(e?.detail || e?.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  const notifyChrome = () =>
    window.dispatchEvent(new Event("reviewer-profile-updated"));

  const handleSaveNames = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await updateData(USERS.USER_INFO, {
        first_name: values.first_name,
        last_name: values.last_name,
      });
      antMessage.success("Profile updated");
      setEditingName(false);
      await load();
      notifyChrome();
    } catch (e) {
      if (e?.errorFields) return;
      antMessage.error(
        typeof e === "string" ? e : e?.detail || e?.message || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  // Upload the picked image through the shared upload endpoint, then
  // persist the returned URL on the profile.
  const handleImagePick = async (file) => {
    if (!file.type?.startsWith("image/")) {
      antMessage.error("Pick an image file (PNG / JPG).");
      return false;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      antMessage.error(`Image must be under ${MAX_IMAGE_MB} MB.`);
      return false;
    }
    setUploading(true);
    try {
      const payload = new FormData();
      payload.append("is_private", "0");
      payload.append("file", file, file.name);
      const token = getEmbedToken() || localStorage.getItem("authToken") || "";
      const resp = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/api/v1/files/upload",
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const fileURL = resp?.data?.file_url;
      if (!fileURL) throw new Error("Upload did not return a file URL");
      await updateData(USERS.USER_INFO, { profile_image_url: fileURL });
      antMessage.success("Profile photo updated");
      await load();
      notifyChrome();
    } catch (e) {
      antMessage.error(
        typeof e === "string" ? e : e?.detail || e?.message || "Upload failed",
      );
    } finally {
      setUploading(false);
    }
    // We handled the upload ourselves — stop antd's default action.
    return false;
  };

  const initials = (() => {
    const name = me?.full_name || me?.email || "";
    const parts = String(name).trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return String(name).slice(0, 2).toUpperCase() || "RU";
  })();

  if (loading && !me) {
    return (
      <Card>
        <Skeleton active avatar paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-4">
        <Title level={3} className="mb-1!">
          My Profile
        </Title>
        <Paragraph className="mb-0! text-slate-500">
          Your reviewer account details. Name and photo appear across the
          review workspace.
        </Paragraph>
      </div>

      <Card className="mb-4" styles={{ body: { padding: 24 } }}>
        <div className="flex items-center gap-6 flex-wrap">
          <Avatar
            size={96}
            src={me?.profile_image_url || undefined}
            style={{ backgroundColor: "#1e3a8a", fontSize: 32 }}
          >
            {initials}
          </Avatar>
          <div>
            <div className="mb-2">
              <Text strong className="text-lg">
                {me?.full_name || "-"}
              </Text>
              <div>
                <Text type="secondary">{me?.email}</Text>
              </div>
              <Tag color="geekblue" className="mt-1">
                Review User
              </Tag>
            </div>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleImagePick}
              disabled={uploading}
            >
              <Button icon={<CameraOutlined />} loading={uploading}>
                {me?.profile_image_url ? "Change photo" : "Upload photo"}
              </Button>
            </Upload>
          </div>
        </div>
      </Card>

      <Card
        title="Name"
        styles={{ body: { padding: 24 } }}
        extra={
          !editingName && (
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                // Seed the form from the loaded profile each time edit
                // opens, so a previous cancelled edit leaves no residue.
                form.setFieldsValue({
                  first_name: me?.first_name || "",
                  last_name: me?.last_name || "",
                });
                setEditingName(true);
              }}
            >
              Edit
            </Button>
          )
        }
      >
        {!editingName ? (
          <div className="max-w-md">
            <div className="mb-3">
              <Text type="secondary" className="block text-xs">
                First name
              </Text>
              <Text>{me?.first_name || "-"}</Text>
            </div>
            <div>
              <Text type="secondary" className="block text-xs">
                Last name
              </Text>
              <Text>{me?.last_name || "-"}</Text>
            </div>
          </div>
        ) : (
          <Form form={form} layout="vertical" className="max-w-md">
            <Form.Item
              name="first_name"
              label="First name"
              rules={[{ required: true, min: 1, message: "Enter your first name" }]}
            >
              <Input allowClear autoFocus />
            </Form.Item>
            <Form.Item name="last_name" label="Last name">
              <Input allowClear />
            </Form.Item>
            <Space>
              <Button
                type="primary"
                className="header-matched-primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={handleSaveNames}
              >
                Save
              </Button>
              <Button
                disabled={saving}
                onClick={() => setEditingName(false)}
              >
                Cancel
              </Button>
            </Space>
          </Form>
        )}
      </Card>
    </div>
  );
}
